import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import MaterialIcon from './MaterialIcon'
import { onboardingChipClass } from '../../lib/onboarding/formStyles'
import { KILO } from '../../constants/KILO_THEME'
import { formatWorkoutTimeDisplay, normalizeTimeHHMM } from '../../lib/trainingSchedule'

const WORKOUT_TIME_PRESETS = ['06:00', '07:00', '12:00', '17:00', '18:30'] as const
const HOURS = Array.from({ length: 24 }, (_, i) => i)
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5)

type CenteredTimePickerProps = {
  label: string
  value: string | null
  icon?: string
  onSave: (value: string) => Promise<void>
}

function parseTime(value: string | null): { hour: number; minute: number } {
  const normalized = normalizeTimeHHMM(value ?? '07:00')
  const [hourRaw, minuteRaw] = normalized.split(':').map(Number)
  const hour = Number.isFinite(hourRaw) ? hourRaw : 7
  const minute = Number.isFinite(minuteRaw) ? Math.round(minuteRaw / 5) * 5 : 0
  return { hour, minute: minute % 60 }
}

function toTimeString(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

type ScrollColumnProps = {
  values: number[]
  selected: number
  onSelect: (value: number) => void
  format: (value: number) => string
}

function ScrollColumn({ values, selected, onSelect, format }: ScrollColumnProps) {
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = listRef.current
    if (!container) return
    const active = container.querySelector<HTMLElement>(`[data-value="${selected}"]`)
    active?.scrollIntoView({ block: 'center', behavior: 'auto' })
  }, [selected])

  return (
    <div
      ref={listRef}
      className="h-52 w-20 snap-y snap-mandatory overflow-y-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <div className="py-20">
        {values.map((value) => {
          const active = value === selected
          return (
            <button
              key={value}
              type="button"
              data-value={value}
              onClick={() => onSelect(value)}
              className={`flex h-12 w-full snap-center items-center justify-center rounded-xl text-xl font-semibold transition-colors ${
                active ? 'text-kilo-accent' : `${KILO.text.muted} hover:text-kilo-text`
              }`}
            >
              {format(value)}
            </button>
          )
        })}
      </div>
    </div>
  )
}

const CenteredTimePicker = ({ label, value, icon = 'schedule', onSave }: CenteredTimePickerProps) => {
  const { t, i18n } = useTranslation()
  const initial = parseTime(value)
  const [hour, setHour] = useState(initial.hour)
  const [minute, setMinute] = useState(initial.minute)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const next = parseTime(value)
    setHour(next.hour)
    setMinute(next.minute)
  }, [value])

  const draft = toTimeString(hour, minute)
  const resolved = value ? normalizeTimeHHMM(value) : null
  const dirty = draft !== resolved

  const handleSave = async () => {
    setSaving(true)
    await onSave(draft)
    setSaving(false)
  }

  return (
    <div className="flex min-h-[min(70vh,32rem)] flex-col">
      <div className="flex flex-1 flex-col items-center justify-center px-2">
        <MaterialIcon name={icon} size="xl" className="mb-6 text-kilo-accent" />
        <p className={`mb-4 text-xs font-bold uppercase tracking-widest ${KILO.text.soft}`}>{label}</p>
        <p className={`mb-6 text-3xl font-bold ${KILO.text.primary}`}>
          {formatWorkoutTimeDisplay(draft, i18n.language)}
        </p>

        <div className="mb-6 grid w-full max-w-sm grid-cols-2 gap-2 sm:grid-cols-3">
          {WORKOUT_TIME_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => {
                const parsed = parseTime(preset)
                setHour(parsed.hour)
                setMinute(parsed.minute)
              }}
              className={onboardingChipClass(draft === preset)}
            >
              {t(`onboarding.logistics.workoutTimePreset.${preset.replace(':', '')}`)}
            </button>
          ))}
        </div>

        <div className="relative flex items-center justify-center gap-2">
          <div className="pointer-events-none absolute inset-x-8 top-1/2 h-12 -translate-y-1/2 rounded-xl border border-kilo-accent/20 bg-kilo-accent/5" />
          <ScrollColumn values={HOURS} selected={hour} onSelect={setHour} format={(v) => String(v).padStart(2, '0')} />
          <span className={`pb-1 text-2xl font-bold ${KILO.text.muted}`}>:</span>
          <ScrollColumn values={MINUTES} selected={minute} onSelect={setMinute} format={(v) => String(v).padStart(2, '0')} />
        </div>
      </div>

      {dirty ? (
        <div className="flex shrink-0 gap-2 border-t border-kilo-border pt-4">
          <button
            type="button"
            onClick={() => {
              const next = parseTime(value)
              setHour(next.hour)
              setMinute(next.minute)
            }}
            className={`flex-1 rounded-xl border border-kilo-border py-3 text-sm font-medium ${KILO.text.muted} transition-colors hover:border-kilo-accent/30`}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSave()}
            className="flex-1 rounded-xl bg-kilo-accent py-3 text-sm font-bold text-black transition-opacity disabled:opacity-50"
          >
            {saving ? t('common.working') : 'Save'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default CenteredTimePicker
